import React, { useCallback, useEffect, useState } from 'react';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import { Badge, Button, Card, Col, Dropdown, Form, InputGroup, Row, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axiosInstance from '../services/axiosInterceptor';
import Swal from 'sweetalert2';
import useStudyYearSelector from '../discipline/useStudyYearSelector';
import { generalClassText } from '../discipline/leaveOptions';
import { useAuth } from '../services/authProvider';
import { HealthFlags, OUTCOMES, outcomeBadge, sourceLabel, stayText, thaiDate, timeOf, todayISO } from './nurseOptions';
import ThaiDateInput from '../components/ThaiDateInput'

const firstOfMonth = () => `${todayISO().slice(0, 7)}-01`;

export default function NurseVisitList() {
  const setSkin = useState(localStorage.getItem('skin-mode') ? 'dark' : '')[1];
  const { yearList, yearId, yearLabel, handleSelectYear } = useStudyYearSelector();

  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(todayISO());
  const [outcome, setOutcome] = useState('');
  const [symptomId, setSymptomId] = useState('');
  const [keyword, setKeyword] = useState('');
  const [term, setTerm] = useState('');

  const [symptoms, setSymptoms] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // ลบได้เฉพาะพยาบาล/แอดมิน — ครูหอพักบันทึกผิดให้แก้ ไม่ใช่ลบทั้งแถว
  // (server บังคับอีกชั้นที่ nurseRoute — ซ่อนปุ่มอย่างเดียวยังยิง endpoint ตรงได้)
  const { posIds } = useAuth();
  const canDelete = (posIds || []).some(p => p === 28 || p === 99);

  useEffect(() => {
    axiosInstance.get('/api/nurse/symptoms')
      .then(res => setSymptoms(res.data || []))
      .catch(() => setSymptoms([]));
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setTerm(keyword.trim()), 350);
    return () => clearTimeout(timer);
  }, [keyword]);

  const fetchRows = useCallback(() => {
    if (!yearId) return;
    setLoading(true);
    const params = new URLSearchParams({ yearId, from, to, limit: 500 });
    if (outcome) params.set('outcome', outcome);
    if (symptomId) params.set('symptomId', symptomId);
    if (term) params.set('keyword', term);
    axiosInstance.get(`/api/nurse/visits?${params.toString()}`)
      .then(res => setRows(res.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [yearId, from, to, outcome, symptomId, term]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const removeVisit = async (row) => {
    const confirm = await Swal.fire({
      icon: 'warning',
      title: 'ลบบันทึกนี้?',
      html: `<div class="fw-semibold">${row.st_name}</div>`
        + `<div>${thaiDate(row.nv_date)} ${timeOf(row.nv_check_in)} น.</div>`
        + (row.nv_leave_id ? '<div class="text-danger mt-2">ใบลาป่วยที่ระบบแจ้งให้จะถูกลบไปด้วย</div>' : '')
        + (row.nv_ref_id ? '<div class="text-secondary mt-2">ใบผ่านประตูที่ออกไปแล้วจะยังอยู่</div>' : ''),
      showCancelButton: true,
      confirmButtonText: 'ลบ',
      cancelButtonText: 'ปิด',
      confirmButtonColor: '#dc3545',
      reverseButtons: true,
    });
    if (!confirm.isConfirmed) return;
    axiosInstance.delete(`/api/nurse/visits/${row.nv_id}`)
      .then(() => {
        fetchRows();
        Swal.fire({ icon: 'success', title: 'ลบแล้ว', timer: 1300, showConfirmButton: false });
      })
      .catch(err => Swal.fire({ icon: 'error', title: 'ไม่สามารถลบได้', text: err?.response?.data?.message || '' }));
  };

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-3">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item">ห้องพยาบาล</li>
              <li className="breadcrumb-item active">ประวัติการมาห้องพยาบาล</li>
            </ol>
            <h4 className="main-title mb-0">ประวัติการมาห้องพยาบาล</h4>
          </div>
          <Dropdown className="mt-3 mt-md-0">
            <Dropdown.Toggle variant="primary" id="nurse-list-year">
              <i className="ri-calendar-line me-1"></i>{yearLabel || 'เลือกปีการศึกษา'}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {yearList.map(year => (
                <Dropdown.Item key={year.year_id} onClick={() => handleSelectYear(year.year_id)}>
                  {`ปีการศึกษา ${year.year_year}`}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>

        <Card className="card-one mb-3">
          <Card.Body>
            <Row className="g-2 align-items-end">
              <Col xs={6} md={2}>
                <Form.Label className="fw-semibold fs-xs mb-1">ตั้งแต่</Form.Label>
                <ThaiDateInput size="sm" value={from} onChange={v => setFrom(v)} />
              </Col>
              <Col xs={6} md={2}>
                <Form.Label className="fw-semibold fs-xs mb-1">ถึง</Form.Label>
                <ThaiDateInput size="sm" value={to} onChange={v => setTo(v)} />
              </Col>
              <Col xs={6} md={2}>
                <Form.Label className="fw-semibold fs-xs mb-1">ผลลัพธ์</Form.Label>
                <Form.Select size="sm" value={outcome} onChange={e => setOutcome(e.target.value)}>
                  <option value="">ทั้งหมด</option>
                  {OUTCOMES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Form.Select>
              </Col>
              <Col xs={6} md={2}>
                <Form.Label className="fw-semibold fs-xs mb-1">อาการ</Form.Label>
                <Form.Select size="sm" value={symptomId} onChange={e => setSymptomId(e.target.value)}>
                  <option value="">ทั้งหมด</option>
                  {symptoms.map(s => <option key={s.ns_id} value={s.ns_id}>{s.ns_name}</option>)}
                </Form.Select>
              </Col>
              <Col xs={12} md={4}>
                <Form.Label className="fw-semibold fs-xs mb-1">ค้นหานักเรียน</Form.Label>
                <InputGroup size="sm">
                  <Form.Control value={keyword} onChange={e => setKeyword(e.target.value)}
                    placeholder="เลขประจำตัว หรือชื่อ" />
                  {keyword && (
                    <Button variant="outline-secondary" onClick={() => setKeyword('')} aria-label="ล้างคำค้นหา">
                      <i className="ri-close-line"></i>
                    </Button>
                  )}
                </InputGroup>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="card-one">
          <Card.Header className="d-flex align-items-center">
            <Card.Title as="h6" className="mb-0">
              พบ {rows.length} รายการ
            </Card.Title>
            <Link to="/nurse/visit" className="btn btn-sm btn-success ms-auto">
              <i className="ri-add-line me-1"></i>บันทึกใหม่
            </Link>
          </Card.Header>
          <Card.Body className="p-0">
            {loading ? (
              <div className="text-center text-secondary py-4 fs-sm">กำลังโหลด...</div>
            ) : rows.length === 0 ? (
              <div className="text-center text-secondary py-4 fs-sm">ไม่พบรายการตามเงื่อนไขที่เลือก</div>
            ) : (
              <div className="table-responsive">
                <Table hover className="mb-0 fs-sm">
                  <thead>
                    <tr>
                      <th>วันที่ / เวลา</th>
                      <th>นักเรียน</th>
                      <th>อาการ</th>
                      <th>ผลลัพธ์</th>
                      <th>ผู้บันทึก</th>
                      <th style={{ width: 90 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(v => (
                      <tr key={v.nv_id}>
                        <td className="text-nowrap">
                          <div>{thaiDate(v.nv_date)}</div>
                          <div className="fs-xs text-secondary">
                            {timeOf(v.nv_check_in)}
                            {v.nv_check_out ? ` - ${timeOf(v.nv_check_out)}` : ' - ยังอยู่'}
                            {` (${stayText(v.nv_check_in, v.nv_check_out)})`}
                          </div>
                        </td>
                        <td>
                          <Link to={`/nurse/students/${v.st_id}`} className="fw-semibold">{v.st_name}</Link>
                          <HealthFlags student={v} />
                          <div className="fs-xs text-secondary">
                            {generalClassText(v) && `${generalClassText(v)} · `}{v.st_idnumber} · {sourceLabel(v.nv_source)}
                          </div>
                        </td>
                        <td>
                          {v.ns_name || '-'}
                          {v.nv_symptom_detail && <div className="fs-xs text-secondary">{v.nv_symptom_detail}</div>}
                          {v.nv_temp && <div className="fs-xs">อุณหภูมิ {v.nv_temp}°C</div>}
                        </td>
                        <td>
                          {outcomeBadge(v.nv_outcome)}
                          {v.nv_refer_place && <div className="fs-xs text-secondary">{v.nv_refer_place}</div>}
                          <div className="fs-xs">
                            {v.nv_leave_id && <Badge bg="light" text="dark" className="me-1 fw-normal">แจ้งลาแล้ว</Badge>}
                            {v.nv_ref_id && <Badge bg="light" text="dark" className="fw-normal">ออกใบผ่านประตู</Badge>}
                          </div>
                        </td>
                        <td className="fs-xs text-secondary">{v.recorded_by_name || '-'}</td>
                        <td className="text-end text-nowrap">
                          {v.nv_ref_id && (
                            <a className="btn btn-sm btn-outline-secondary me-1"
                              href={`/nurse/visits/${v.nv_id}/referral`} target="_blank" rel="noreferrer"
                              aria-label="พิมพ์ใบส่งตัว">
                              <i className="ri-printer-line"></i>
                            </a>
                          )}
                          {canDelete && (
                            <Button size="sm" variant="outline-danger" onClick={() => removeVisit(v)} aria-label="ลบบันทึก">
                              <i className="ri-delete-bin-line"></i>
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
        <Footer />
      </div>
    </>
  );
}
